const cloneDeep = require('lodash/cloneDeep');
const { config, actionUtils } = require('@sstdev/lib_metadata-config');
const unique = require('lodash/uniq');
const set = require('lodash/set');
const get = require('lodash/get');
const upperFirst = require('lodash/upperFirst');

let _notificationHandler;
function extract(useCase, redactedMetadata, role, notificationHandler) {
    _notificationHandler = notificationHandler;
    const existingNamespacesRelations = cloneDeep(useCase);
    // Merge in the application namespace
    existingNamespacesRelations.application = existingNamespacesRelations.application ?? {
        _meta: { hideApiDocs: true }
    };
    config.applicationNamespaceAdditions.relations.forEach(rel => {
        existingNamespacesRelations.application[rel.title] = existingNamespacesRelations.application[rel.title] ?? {
            _meta: rel
        };
    });

    const _meta = {
        title: redactedMetadata.title,
        featureFlags: redactedMetadata.featureFlags,
        release: redactedMetadata.release,
        blockId: redactedMetadata.blockId,
        redactedAt: redactedMetadata.redactedAt
    };
    // Add all the properties to the data dictionary tree
    if (redactedMetadata.topMenu) {
        redactedMetadata.topMenu.forEach(hnode => {
            parseTree(hnode, useCase, { observedLocation: hnode.blockId, existingNamespacesRelations }, role);
        });
    }
    if (redactedMetadata.topMenuV2) {
        redactedMetadata.topMenuV2.forEach(hnode => {
            parseTree(hnode, useCase, { observedLocation: hnode.blockId, existingNamespacesRelations }, role);
        });
    }
    if (redactedMetadata.hNodes) {
        parseTree(
            redactedMetadata.hNodes,
            useCase,
            { observedLocation: redactedMetadata.hNodes.blockId, existingNamespacesRelations },
            role
        );
    }
    return { dataDictionary: useCase, _meta };
}

function parseTree(hNode, /* byref */ dataDictionary = {}, actionContext, role, parentHidden) {
    if (!validateHNodeNamespaceRelation(hNode, actionContext)) return;

    //in: node to parse, access context as observed this far, user's role
    //update context
    actionContext = updateActionContext(hNode, dataDictionary, actionContext, role, parentHidden);

    //process current level
    processNode(hNode, /* byref */ dataDictionary, actionContext);
    //process children
    processChildren(hNode, /* byref */ dataDictionary, actionContext, role, parentHidden);
}

function updateActionContext(hNode, dataDictionary, actionContext, role, parentHidden) {
    //Note, actionContext does not define _permission_, it defines for which operation any future found properties are defined.

    //observed location is the closest ancestor in the subtree which modified a value, for help with debugging
    let observedLocation = actionContext.observedLocation;
    const namespace = hNode.namespace || actionContext.namespace;
    const relation = hNode.relation || actionContext.relation;

    const allowedExceptions = config.allowedNamespaceRelationExceptions;
    if (allowedExceptions[namespace] && allowedExceptions[namespace][relation]) {
        return actionContext;
    }

    // `hidden` is used to provide an error about a discrepancy between API permissions and UI visibility - so it only
    // is concerned with role (because feature flags, for instance, are not accounted for in API restrictions in Blockly)
    const hidden = parentHidden || (hNode.hiddenFor != null && hNode.hiddenFor.includes(role));
    function generateErrorMessage(relationTitle, op, blockId) {
        return `For relation "${relationTitle}", role "${role}", the method access for "${op}" is denied, but a more permissive use was found ${
            actionContext.page ? `on page "${actionContext.page}" ` : ''
        } in block: ${blockId}`;
    }

    let relationAccess = {};
    if (namespace && relation) {
        //does this fit within the defined method access?
        relationAccess = getGuaranteed(dataDictionary, [namespace, relation, '_meta', 'accessContext']);

        if (
            !relationAccess.get &&
            !(
                namespace === 'application' &&
                config.applicationNamespaceAdditions.relations.some(r => r.title === relation)
            )
        ) {
            _notificationHandler(
                `path ${namespace}.${relation} was not found in the data dictionary, but it is referenced by a formElement${
                    actionContext.page ? ` on page "${actionContext.page}"` : ''
                }.  This probably means you need to define the namespace, relation (${namespace}, ${relation}) somewhere in your metadata.  Here is the metadata: ${JSON.stringify(
                    hNode
                )}`,
                hNode.blockId
            );
        }
    }

    //is the subtree readonly?
    let readonly = actionContext.readonly;
    if (hNode.readOnlyFor && hNode.readOnlyFor.includes(role)) {
        readonly = true;
        observedLocation = hNode.blockId;
    }
    //figure out what action we are doing here
    let action = readonly ? 'read' : actionContext.action || 'read';
    //are we going into an update or create subtree
    if (hNode.hNodeTypeGroup === 'detailPane') {
        if (hNode.showForAction.startsWith('edit')) {
            action = 'update';
            observedLocation = hNode.blockId;
            // Throw an error if the current block permissions are greater than the relation permissions.
            if (!readonly && !hidden && relationAccess.patch && !relationAccess.patch.value) {
                _notificationHandler(generateErrorMessage(relation, 'PATCH', hNode.blockId), hNode.blockId);
            }
        } else if (hNode.showForAction.startsWith('new')) {
            action = 'create';
            observedLocation = hNode.blockId;
            if (readonly) {
                const message = `For relation "${relation}", role "${role}" a read only access modifier is specified on a create screen ${
                    actionContext.page ? `on page "${actionContext.page}"` : ''
                } ... which probably doesn't make very much sense.`;
                _notificationHandler(message, hNode.blockId);
            }
            // Throw an error if the current block permissions are greater than the relation permissions.
            if (!hidden && relationAccess.post && !relationAccess.post.value) {
                _notificationHandler(generateErrorMessage(relation, 'POST', hNode.blockId), hNode.blockId);
            }
        }
    }
    if (role !== 'DEITY' && ['SingleRecordForm', 'Transaction'].includes(hNode.hNodeType)) {
        action = hNode.forAction && hNode.forAction.startsWith('new') ? 'create' : 'update';
        observedLocation = hNode.blockId;
        if (!readonly && !hidden && relationAccess.patch && !relationAccess.patch.value) {
            _notificationHandler(generateErrorMessage(relation, 'PATCH', hNode.blockId), hNode.blockId);
        }
    }
    //are we in a remove button
    if (hNode.hNodeTypeGroup === 'formButton') {
        if (hNode.formAction === 'remove') {
            const canRemove = !hidden && !readonly;
            // Throw an error if the current block permissions are greater than the relation permissions.
            if (canRemove && relationAccess.remove && !relationAccess.remove.value) {
                _notificationHandler(generateErrorMessage(relation, 'DELETE', hNode.blockId), hNode.blockId);
            }
            if (canRemove && namespace && relation) {
                const relationAccessContext = getGuaranteed(dataDictionary, [namespace, relation, '_meta', 'accessContext']);
                relationAccessContext.remove = {
                    value: true,
                    observedLocation: hNode.blockId
                };
                action = 'remove';
                observedLocation = hNode.blockId;
            }
        }
    }
    const actionTarget = actionUtils.getActionTarget(hNode);
    if (
        !hidden &&
        !readonly &&
        actionTarget?.namespace &&
        actionTarget?.relation &&
        actionTarget?.action &&
        (actionTarget.namespace !== hNode.namespace || actionTarget.relation !== hNode.relation)
    ) {
        grantRelationAccessByAction(
            dataDictionary,
            actionTarget.namespace,
            actionTarget.relation,
            actionTarget.action,
            hNode.blockId
        );
    }

    let page = actionContext.page || '';
    if (['TabNav', 'NavHeading', 'NavigationSelectedContextPage'].includes(hNode.hNodeType)) {
        page = hNode.title;
    }
    const newActionContext = {
        page,
        namespace,
        relation,
        subtree: [...(actionContext.subtree || [])],
        [action]: true,
        action,
        readonly,
        observedLocation,
        existingNamespacesRelations: actionContext.existingNamespacesRelations
    };

    return newActionContext;
}

function grantRelationAccessByAction(dataDictionary, namespace, relation, action, observedLocation) {
    const relationAccessContext = getGuaranteed(dataDictionary, [namespace, relation, '_meta', 'accessContext']);
    const writeAccessContext = actionUtils.buildWriteAccessContextForAction(action, observedLocation);

    Object.entries(writeAccessContext).forEach(([method, access]) => {
        if (access?.value) {
            relationAccessContext[method] = access;
        }
    });
}

function processChildren(hNode, dataDictionary, actionContext, role, parentHidden) {
    if (
        (hNode.hNodeType === 'InvisibleElement' && ['object', 'array'].includes(hNode.overrideDataType)) ||
        [
            'EditableList',
            'MultiPickerSensorGrid',
            'MultiPickerTransactionGrid',
            'WorkflowCriteriaPane',
            'WorkflowActionList',
            'WorkflowActionPane',
            'NestedObject',
            'object'
        ].includes(hNode.hNodeType) ||
        hNode.hNodeTypeGroup === 'workflowCriteriaElement'
    ) {
        let propertyName;
        switch (hNode.hNodeType) {
            case 'MultiPickerDropDownCriteria':
            case 'DropDownCriteria':
                propertyName = `${hNode.foreignNamespace}:${hNode.foreignRelation}`;
                break;
            case 'MultiPickerTransactionGrid':
                propertyName = 'entries';
                break;
            default:
                propertyName = hNode.propertyName;
                break;
        }
        if (propertyName == null) {
            throw new Error(`No propertyName found for ${hNode.hNodeType}`);
        }
        //clone and extend the subtree
        actionContext = cloneDeep(actionContext);

        actionContext.subtree.push(propertyName);
    }
    const children = getChildren(hNode, dataDictionary, actionContext);

    // `hidden` is used to provide an error about a discrepancy between API permissions and UI visibility - so it only
    // is concerned with role (because feature flags, for instance, are not accounted for in API restrictions in Blockly)
    const hidden = parentHidden || (hNode.hiddenFor != null && hNode.hiddenFor.includes(role));
    (children || []).map(child => parseTree(child, dataDictionary, actionContext, role, hidden));
}

/**
 * Some nodes have special/code generated children. Adding those here.
 * @param {Object} hNode
 */
function getChildren(hNode, dataDictionary, actionContext) {
    switch (hNode.hNodeType) {
        // Add a title with the transaction ID so the user doesn't have to think about it in blockly.
        case 'Transaction': {
            return [
                {
                    hNodeType: 'ShortText',
                    hNodeTypeGroup: 'formElement',
                    title: 'Transaction ID',
                    propertyName: 'title',
                    required: true
                },
                ...hNode.children
            ];
        }
        case 'MultiPickerTransactionGrid': {
            const { namespace, relation } = actionContext;
            if (!relation.endsWith('-transaction')) {
                throw new Error(
                    `${
                        actionContext.page ? `On page "${actionContext.page}" ` : ''
                    }MultiPickerTransactionGrid must be within a Transaction form and the relation on the Transaction form should be named like this: "<relation being updated by transaction>-transaction".`
                );
            }
            const mergeRelation = relation.split('-')[0];
            const mergeType = getGuaranteed(dataDictionary, [namespace, mergeRelation]);
            let titleDescription, propertyName;
            if (mergeType.title != null) {
                titleDescription = mergeType.title._meta.title;
                propertyName = 'title';
            } else if (mergeType.assetNo != null) {
                titleDescription = mergeType.assetNo._meta.title;
                propertyName = 'assetNo';
            } else {
                throw new Error(`not implemented - ${mergeRelation} needs alternate title information.`);
            }
            return [
                {
                    hNodeType: 'ShortText',
                    hNodeTypeGroup: 'formElement',
                    title: titleDescription,
                    propertyName,
                    required: true
                }
            ];
        }
        case 'MultiPickerSensorGrid': {
            const mergeType = getGuaranteed(dataDictionary, [hNode.mergeNamespace, hNode.mergeRelation]);
            const titleDescription = mergeType.title
                ? mergeType.title._meta.title
                : mergeType.assetNo
                  ? mergeType.assetNo._meta.title
                  : 'Title';
            return [
                {
                    hNodeType: 'ShortText',
                    hNodeTypeGroup: 'formElement',
                    title: titleDescription,
                    propertyName: 'title',
                    required: true
                }
            ];
        }
        case 'WorkflowActionPane':
            return [
                {
                    hNodeType: 'ShortText',
                    hNodeTypeGroup: 'formElement',
                    title: 'Action Identifier, should be: ' + hNode.propertyName,
                    propertyName: 'workflowAction',
                    required: true
                },
                ...hNode.children
            ];
        case 'ShortTextCriteria':
            //shortText criteria support equal, not equal, contains and startswith
            return [
                {
                    hNodeType: 'ShortText',
                    hNodeTypeGroup: 'formElement',
                    title: 'Test for equality',
                    propertyName: '_eq',
                    required: false,
                    minLength: hNode.minLength,
                    maxLength: hNode.maxLength
                },
                {
                    hNodeType: 'ShortText',
                    hNodeTypeGroup: 'formElement',
                    title: 'Test for inequality',
                    propertyName: '_neq',
                    required: false,
                    minLength: hNode.minLength,
                    maxLength: hNode.maxLength
                },
                {
                    hNodeType: 'ShortText',
                    hNodeTypeGroup: 'formElement',
                    title: 'Test property for containing specified value',
                    propertyName: '_contains',
                    required: false,
                    minLength: hNode.minLength,
                    maxLength: hNode.maxLength
                },
                {
                    hNodeType: 'ShortText',
                    hNodeTypeGroup: 'formElement',
                    title: 'Test property to start with specified value',
                    propertyName: '_startsWith',
                    required: false,
                    minLength: hNode.minLength,
                    maxLength: hNode.maxLength
                }
            ];
        case 'DropDownCriteria': {
            const nestedProperties = distinct([
                hNode.propertyName,
                ...(hNode.dropdownDisplayProperties || []),
                ...(hNode.inputDisplayProperties || [])
            ]);
            const children = nestedProperties.reduce(
                (soFar, propertyName) => {
                    return [
                        ...soFar,
                        {
                            hNodeType: 'ShortText',
                            hNodeTypeGroup: 'formElement',
                            title: propertyName,
                            propertyName: propertyName,
                            required: true
                        }
                    ];
                },
                [
                    {
                        hNodeType: 'ShortText',
                        hNodeTypeGroup: 'formElement',
                        title: 'Unique ID',
                        propertyName: '_id',
                        required: true
                    }
                ]
            );
            //dropdown criteria support equal, not equal
            return [
                {
                    hNodeType: 'NestedObject',
                    hNodeTypeGroup: 'formElement',
                    title: 'Test for equality',
                    propertyName: '_eq',
                    required: false,
                    children
                },
                {
                    hNodeType: 'NestedObject',
                    hNodeTypeGroup: 'formElement',
                    title: 'Test for inequality',
                    propertyName: '_neq',
                    required: false,
                    children
                }
            ];
        }
        case 'MultiPickerDropDownCriteria': {
            const nestedProperties = distinct([
                ...(hNode.dropdownDisplayProperties || []),
                ...(hNode.inputDisplayProperties || [])
            ]);
            const children = nestedProperties.reduce(
                (soFar, propertyName) => {
                    return [
                        ...soFar,
                        {
                            hNodeType: 'ShortText',
                            hNodeTypeGroup: 'formElement',
                            title: propertyName,
                            propertyName: propertyName,
                            required: true
                        }
                    ];
                },
                [
                    {
                        hNodeType: 'ShortText',
                        hNodeTypeGroup: 'formElement',
                        title: 'Unique ID',
                        propertyName: '_id',
                        required: true
                    }
                ]
            );
            //dropdown criteria support equal, not equal
            return [
                {
                    hNodeType: 'EditableList',
                    hNodeTypeGroup: 'formElement',
                    title: 'Test for inclusion',
                    propertyName: '_in',
                    required: false,
                    children
                },
                {
                    hNodeType: 'EditableList',
                    hNodeTypeGroup: 'formElement',
                    title: 'Test for exclusion',
                    propertyName: '_nin',
                    required: false,
                    children
                }
            ];
        }
        case 'IntegerCriteria':
            //integer criteria support equal, greater than, less than
            return [
                {
                    hNodeType: 'Integer',
                    hNodeTypeGroup: 'formElement',
                    title: 'Test for equality',
                    propertyName: '_eq',
                    required: false,
                    minimum: hNode.minimum,
                    maximum: hNode.maximum
                },
                {
                    hNodeType: 'Integer',
                    hNodeTypeGroup: 'formElement',
                    title: 'Test for greater than',
                    propertyName: '_gt',
                    required: false,
                    minimum: hNode.minimum,
                    maximum: hNode.maximum
                },
                {
                    hNodeType: 'Integer',
                    hNodeTypeGroup: 'formElement',
                    title: 'Test for less than',
                    propertyName: '_lt',
                    required: false,
                    minimum: hNode.minimum,
                    maximum: hNode.maximum
                }
            ];
        default:
            return hNode.children;
    }
}

function processNode(hNode, dataDictionary = {}, actionContext) {
    if (hNode.hNodeType && hNode.hNodeType.includes('DropDown') && hNode.propertyName == null) {
        hNode.propertyName = 'title';
    }
    if (
        ['formElement', 'workflowCriteriaElement', 'workflowPane', 'InvisibleElement'].includes(hNode.hNodeTypeGroup) &&
        hNode.propertyName &&
        hNode.propertyName.includes('.')
    ) {
        //if there is a "." in the property, we need to nest it
        const [first, second, ...rest] = hNode.propertyName.split('.');
        let children = [];
        let virtualHNode = {
            hNodeType: 'InvisibleElement',
            hNodeTypeGroup: 'formElement',
            propertyName: first,
            title: '',
            required: hNode.required
        };
        //hNode.hNodeType === 'InvisibleElement' && ['object', 'array'].includes(hNode.overrideDataType)
        //if there is a rest, AND second is numeric, we need to nest in an array
        if (rest && rest.length && isFinite(second)) {
            virtualHNode.overrideDataType = 'array';
            virtualHNode.overrideDataFormat = 'object';
            children.push({ ...hNode, propertyName: rest.join('.') });
        } else {
            //otherwise, we need to nest in an object.
            virtualHNode.overrideDataType = 'object';
            children.push({ ...hNode, propertyName: [second, ...rest].join('.') });
        }
        processNode(virtualHNode, dataDictionary, actionContext);
        processChildren({ ...virtualHNode, children }, dataDictionary, actionContext);
        return;
    }
    //if this is a formElement, extract the property
    if (
        ['formElement', 'workflowCriteriaElement', 'workflowPane'].includes(hNode.hNodeTypeGroup) ||
        hNode.hNodeType === 'MultiPickerTransactionGrid'
    ) {
        //special case:
        if (['DropDown', 'DropDownWithFilter', 'Image'].includes(hNode.hNodeType)) {
            return registerForeignNsRelProperty(hNode, dataDictionary, actionContext);
        }

        let propertyName;
        switch (hNode.hNodeType) {
            case 'MultiPickerTransactionGrid':
                propertyName = 'entries';
                break;
            case 'MultiPickerDropDownCriteria':
            case 'DropDownCriteria':
                propertyName = `${hNode.foreignNamespace}:${hNode.foreignRelation}`;
                break;
            default:
                propertyName = hNode.propertyName;
        }

        //stuff it into the dataDictionary in the right place
        let existing = getGuaranteed(dataDictionary, [
            actionContext.namespace,
            actionContext.relation,
            ...actionContext.subtree,
            propertyName
        ]);
        //if we encountered this property before, update the access
        if (existing._meta) {
            //if we didn't have an id yet, it means the hNode was a virtual one from a dropdown.
            //if now we do have one, this is an actual hNode.
            //So, fold in the extra/actual data
            if (!existing._meta.id && hNode.id) {
                existing._meta = {
                    ...existing._meta,
                    ...hNode
                };
            }
            if (!existing._meta.accessContext.patch.value && actionContext.update) {
                existing._meta.accessContext.patch = {
                    value: true,
                    observedLocation: actionContext.observedLocation
                };
            }
            if (!existing._meta.accessContext.post.value && actionContext.create) {
                existing._meta.accessContext.post = {
                    value: true,
                    observedLocation: actionContext.observedLocation
                };
            }
            if (!existing._meta.accessContext.remove.value && actionContext.remove) {
                existing._meta.accessContext.remove = {
                    value: true,
                    observedLocation: actionContext.observedLocation
                };
            }
        } else {
            //otherwise create a new entry
            const parent = getGuaranteed(dataDictionary, [
                actionContext.namespace,
                actionContext.relation,
                ...actionContext.subtree
            ]);
            parent[propertyName] = {
                _meta: Object.assign(
                    hNode, //register self
                    {
                        //register access
                        accessContext: {
                            get: { value: true, observedLocation: hNode.blockId },
                            patch: { value: !!actionContext.update, observedLocation: actionContext.observedLocation },
                            post: { value: !!actionContext.create, observedLocation: actionContext.observedLocation },
                            put: { value: false }, // we never PUT anything
                            remove: { value: !!actionContext.remove, observedLocation: actionContext.observedLocation }
                        }
                    }
                )
            };
        }
    }
}

function registerForeignNsRelProperty(hNode, dataDictionary, actionContext) {
    let {
        title: _title,
        foreignNamespace,
        foreignRelation,
        propertyName,
        dropdownDisplayProperties = [],
        inputDisplayProperties = []
    } = hNode;
    const allProperties = unique([...dropdownDisplayProperties, ...inputDisplayProperties, propertyName]);
    // Assume every existing relation has a title/titleAlternative even if not updated somewhere in this UI
    // For example, 'Role' titles in security will almost never be updated in the UI, but they must
    // often be displayed in dropdowns.
    //register this as a read access to foreignNamespace.foreignRelation.propertyName if it wasn't there yet:
    allProperties.forEach(propertyName => {
        processNode(
            {
                hNodeType: 'ShortText',
                hNodeTypeGroup: 'formElement',
                title: _title,
                propertyName,
                required: false
            },
            dataDictionary,
            {
                namespace: foreignNamespace || hNode.namespace || actionContext.namespace,
                relation: foreignRelation || hNode.relation || actionContext.relation,
                subtree: [],
                read: true,
                action: 'read'
            }
        );

        let dictionaryPropertyName = `${foreignNamespace}:${foreignRelation}`;

        let existing = getGuaranteed(dataDictionary, [
            actionContext.namespace,
            actionContext.relation,
            ...actionContext.subtree,
            dictionaryPropertyName
        ]);
        const accessContext = {
            get: { value: true, observedLocation: hNode.blockId },
            patch: { value: !!actionContext.update, observedLocation: actionContext.observedLocation },
            post: { value: !!actionContext.create, observedLocation: actionContext.observedLocation },
            put: { value: false }, // we never PUT anything
            remove: { value: !!actionContext.remove, observedLocation: actionContext.observedLocation }
        };

        let title, nestingObjectAccess;
        // A nested property exists and is defined as a string
        // (e.g. 'metaui:useCase.title')
        if (propertyName.includes(':')) {
            // extract the namespace, relation and propertyName from the string
            let relation,
                subPropertyNames = [];
            const [namespace, remainder] = propertyName.split(':');
            if (remainder.includes('.')) {
                [relation, ...subPropertyNames] = remainder.split('.');
            } else {
                throw new Error(
                    `When registering foreign key for data dictionary, a foreignKey property of ${propertyName} was specified. This includes a namespace and relation, but no actual property.`
                );
            }
            if (subPropertyNames.length > 1) {
                throw new Error(
                    `When registering foreign key for data dictionary, a foreignKey property of ${propertyName} was specified. This includes a deeply nested property which is not supported.`
                );
            }

            // In the data dictionary, create the object (with _meta) which contains
            // the nested property (if it doesn't already exist).
            const metaPath = `${namespace}:${relation}._meta`;
            if (!get(existing, metaPath)) {
                set(existing, metaPath, {
                    title: dataDictionary[namespace][relation]._meta.prettyName,
                    hNodeType: 'object',
                    hNodeTypeGroup: 'formElement',
                    propertyName: `${namespace}:${relation}`,
                    accessContext
                });
            }
            // Get the access of the object containing the nested property, so
            // it can be changed to match the actual nested property (below)
            nestingObjectAccess = get(existing, metaPath + '.accessContext');
            title = upperFirst(subPropertyNames[0]);
        } else {
            title = allProperties.length === 1 ? _title : upperFirst(propertyName);
        }

        if (existing._meta) {
            let property = get(existing, propertyName);
            if (!property || !property._meta) {
                set(existing, propertyName, {
                    _meta: {
                        hNodeType: 'ShortText',
                        hNodeTypeGroup: 'formElement',
                        title,
                        required: false,
                        accessContext
                    }
                });
                property = get(existing, propertyName);
            }
            if (!existing._meta.accessContext.patch.value && actionContext.update) {
                const access = {
                    value: true,
                    observedLocation: actionContext.observedLocation
                };
                existing._meta.accessContext.patch = access;
                property._meta.accessContext.patch = access;
                if (nestingObjectAccess) {
                    nestingObjectAccess.patch = access;
                }
            }
            if (!existing._meta.accessContext.post.value && actionContext.create) {
                const access = {
                    value: true,
                    observedLocation: actionContext.observedLocation
                };
                existing._meta.accessContext.post = access;
                property._meta.accessContext.post = access;
                if (nestingObjectAccess) {
                    nestingObjectAccess.post = access;
                }
            }
            if (!existing._meta.accessContext.remove.value && actionContext.remove) {
                const access = {
                    value: true,
                    observedLocation: actionContext.observedLocation
                };
                existing._meta.accessContext.remove = access;
                property._meta.accessContext.remove = access;
                if (nestingObjectAccess) {
                    nestingObjectAccess.remove = access;
                }
            }
        } else {
            const parent = getGuaranteed(dataDictionary, [
                actionContext.namespace,
                actionContext.relation,
                ...actionContext.subtree
            ]);
            parent[dictionaryPropertyName]._meta = { ...hNode, accessContext };
            set(parent[dictionaryPropertyName], propertyName, {
                _meta: {
                    hNodeType: 'ShortText',
                    hNodeTypeGroup: 'formElement',
                    title,
                    required: false,
                    accessContext
                }
            });
        }
    });
}

//https://stackoverflow.com/a/5484764
//Get a reference to the property at the end of keyPath
//create any intermediate properties as necessary
function getGuaranteed(obj, keyPath) {
    const lastKeyIndex = keyPath.length - 1;
    for (let i = 0; i < lastKeyIndex; ++i) {
        const key = keyPath[i];
        obj[key] = obj[key] || {};
        obj = obj[key];
    }
    obj[keyPath[lastKeyIndex]] = obj[keyPath[lastKeyIndex]] || {};
    return obj[keyPath[lastKeyIndex]];
}

function validateHNodeNamespaceRelation(hNode, actionContext) {
    const ns = hNode.namespace || hNode.defaultNamespace || hNode.foreignNamespace;
    const rel = hNode.relation || hNode.defaultRelation || hNode.foreignRelation;
    const allowedExceptions = config.allowedNamespaceRelationExceptions;
    if (ns && rel) {
        const allowed = actionContext.existingNamespacesRelations;
        if (allowedExceptions[ns] && allowedExceptions[ns][rel]) {
            //Right now this is the only expected exception,
            //the only formElement that has a different namespace/relation
            //but where we DO need to continue:
            if (['MultiPickerSensorGrid', 'MultiPickerTransactionGrid'].includes(hNode.hNodeType)) {
                //special case, do continue
                return true;
            }
            //otherwise do ignore
            return false;
        }
        if (!allowed[ns] || !allowed[ns][rel]) {
            _notificationHandler(
                `${actionContext.page ? `On page "${actionContext.page}" ` : ''}${
                    hNode.title || hNode.hNodeType
                } uses namespace ${ns} and relation ${rel}, but that combination does not exist.`,
                hNode.blockId
            );
            throw new Error('Aborting data dictionary creation.  See validation errors for details.');
        }
    }
    return true;
}

function distinct(arrayOfStrings) {
    return [...new Set(arrayOfStrings)].filter(x => !!x);
}

module.exports = {
    extract,
    _private: {
        registerForeignNsRelProperty
    }
};
